// Azure AI Foundry "Responses" API (OpenAI v1-compatible), streamed.
// The endpoint's CORS only allows the `api-key`, `content-type` and
// `authorization` request headers, so tuning options must go in the body,
// never in headers.
export const AZURE_RESPONSES_URL =
  'https://genai-001.services.ai.azure.com/openai/v1/responses';
export const QUICK_SUMMARY_MODEL = 'gpt-5.4-nano';

export class AzureResponsesError extends Error {
  constructor(message, { status, code } = {}) {
    super(message);
    this.name = 'AzureResponsesError';
    this.status = status;
    this.code = code;
  }
}

// The Responses API reports usage as input_tokens/output_tokens; the token
// usage screens (TokenUsageView, tokenUsageTracker) read the chat-completions
// names, so saved usage is converted to those.
function normalizeUsage(usage) {
  if (!usage) return undefined;
  return {
    prompt_tokens: usage.input_tokens ?? 0,
    completion_tokens: usage.output_tokens ?? 0,
    total_tokens: usage.total_tokens ?? 0,
  };
}

/**
 * Streams one answer. `onDelta(textSoFar)` fires as text arrives (optional -
 * callers that only want the finished answer can omit it); resolves with the
 * full text plus the response id, creation time (epoch seconds) and usage
 * once the stream completes.
 * Throws AzureResponsesError (code 'missing-api-key' before any request is
 * made, or an HTTP/stream error) so callers can show something other than a
 * blank panel.
 */
export async function streamAzureResponse({
  apiKey,
  model = QUICK_SUMMARY_MODEL,
  instructions,
  input,
  onDelta,
  signal,
}) {
  if (!apiKey) {
    throw new AzureResponsesError('Missing API key', { code: 'missing-api-key' });
  }

  const response = await fetch(AZURE_RESPONSES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'api-key': apiKey },
    body: JSON.stringify({ model, instructions, input, stream: true }),
    signal,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.error?.message || message;
    } catch (e) {
      // non-JSON error body - keep the status-only message
    }
    throw new AzureResponsesError(message, { status: response.status });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let id;
  let created;
  let usage;

  const handleEvent = (block) => {
    const data = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trim())
      .join('\n');
    if (!data || data === '[DONE]') return;
    let evt;
    try {
      evt = JSON.parse(data);
    } catch (e) {
      return;
    }
    switch (evt.type) {
      case 'response.output_text.delta':
        text += evt.delta || '';
        onDelta?.(text);
        break;
      case 'response.completed':
      case 'response.incomplete':
        id = evt.response?.id;
        created = evt.response?.created_at;
        usage = normalizeUsage(evt.response?.usage);
        break;
      case 'response.failed':
        throw new AzureResponsesError(
          evt.response?.error?.message || 'The AI response failed',
          { code: evt.response?.error?.code },
        );
      case 'error':
        throw new AzureResponsesError(evt.message || 'The AI stream failed', {
          code: evt.code,
        });
      default:
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      handleEvent(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
    }
  }
  if (buffer.trim()) handleEvent(buffer);

  if (!text) {
    throw new AzureResponsesError('The AI returned an empty answer');
  }
  return { text, id, created, usage };
}

/**
 * Title + message for the error dialogs the callers already show, so a failed
 * call says why instead of failing silently.
 */
export function describeAzureError(error) {
  if (error?.code === 'missing-api-key') {
    return {
      title: 'Not configured',
      message:
        "This AI model isn't configured on this environment yet (missing API key).",
    };
  }
  if (error?.status === 401 || error?.status === 403) {
    return {
      title: `Error : ${error.status}`,
      message:
        "The AI service rejected the request. Please contact support.",
    };
  }
  return {
    title: 'Error',
    message:
      "The AI service couldn't answer right now. Please try again in a moment.",
  };
}
